import { useState } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/ConsentNotice.css';

// Consent record schema — stored in localStorage as `garuda_consent`
export interface ConsentRecord {
  givenAt: string;     // ISO timestamp
  version: string;     // consent notice version — bump when notice changes materially
  ageConfirmed: boolean;
}

const CONSENT_KEY   = 'garuda_consent';
const CONSENT_VER   = '1.0';

export function getConsentRecord(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw) as ConsentRecord;
    // Invalidate if from an older version of the notice
    return rec.version === CONSENT_VER ? rec : null;
  } catch {
    return null;
  }
}

export function clearConsentRecord(): void {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    // Safari private mode
  }
}

interface ConsentNoticeProps {
  onAccept: () => void;
  onDecline: () => void;
}

export default function ConsentNotice({ onAccept, onDecline }: ConsentNoticeProps) {
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { t } = useGlossary();

  const handleAccept = () => {
    const record: ConsentRecord = {
      givenAt: new Date().toISOString(),
      version: CONSENT_VER,
      ageConfirmed: true,
    };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    } catch {
      // Safari private mode — still allow the session to continue
    }
    onAccept();
  };

  return (
    <div className="cn-overlay" role="dialog" aria-modal="true" aria-labelledby="cn-title">
      <div className="cn-card">
        <div className="cn-badge">{t('consent.badge')}</div>
        <h2 className="cn-title" id="cn-title">{t('consent.title')}</h2>
        <p className="cn-subtitle">
          {t('consent.subtitle', { name: 'StudentTaaS / Garuda XaaS Portal' })}
        </p>

        {/* ── What we collect ── */}
        <div className="cn-section">
          <div className="cn-section-title">{t('consent.collect.title')}</div>
          <ul className="cn-list">
            <li>
              <strong>Firebase Authentication (Google):</strong> {t('consent.collect.firebase')}
            </li>
            <li>
              <strong>अध्ययन द्विआङ्कीय आत्मरूप (PII-मुक्त):</strong> {t('consent.collect.profile')}
            </li>
            <li>
              <strong>सहयोग द्विआङ्कीय आत्मरूप (वैकल्पिक):</strong> {t('consent.collect.discovery')}
            </li>
          </ul>
        </div>

        {/* ── Purpose ── */}
        <div className="cn-section">
          <div className="cn-section-title">{t('consent.purpose.title')}</div>
          <p className="cn-text">{t('consent.purpose.body')}</p>
        </div>

        {/* ── Rights ── */}
        <div className="cn-section">
          <div className="cn-section-title">{t('consent.rights.title')}</div>
          <ul className="cn-list">
            <li>{t('consent.rights.profile')}</li>
            <li>{t('consent.rights.download')}</li>
            <li>{t('consent.rights.delete')}</li>
            <li>
              {t('consent.rights.contact')}{' '}
              <a href="mailto:contact@raajadharma.org" className="cn-link">
                contact@raajadharma.org
              </a>
            </li>
          </ul>
        </div>

        {/* ── Expandable: cross-border transfer notice ── */}
        <button className="cn-expand-btn" onClick={() => setExpanded(v => !v)}>
          {expanded ? '▲' : '▼'} {t('consent.transfer.button')}
        </button>
        {expanded && (
          <div className="cn-expand-body">
            <p>{t('consent.transfer.body')}</p>
          </div>
        )}

        {/* ── Age gate ── */}
        <label className={`cn-age-label ${ageConfirmed ? 'cn-age-label--checked' : ''}`}>
          <input
            type="checkbox"
            checked={ageConfirmed}
            onChange={e => setAgeConfirmed(e.target.checked)}
            className="cn-age-checkbox"
          />
          <span>
            {t('consent.age.text')}
          </span>
        </label>

        {/* ── Actions ── */}
        <div className="cn-actions">
          <button
            className="cn-btn-accept"
            disabled={!ageConfirmed}
            onClick={handleAccept}
          >
            {t('consent.accept')}
          </button>
          <button className="cn-btn-decline" onClick={onDecline}>
            {t('consent.decline')}
          </button>
        </div>

        <p className="cn-footnote">{t('consent.footnote')}</p>
      </div>
    </div>
  );
}
